import type { QueryClient } from '@tanstack/react-query'
import {
  clearTokenFromLocalStorage,
  isOnCFDomain,
  logOut,
  saveToken,
  saveTokenToLocalStorage,
} from '@repo/auth-utils'
import { usesFirstPartySessionBridge } from '@repo/first-party-session'
import { invalidateToken, saveRpcToken } from '@repo/rpc-codegen/fetch-rpc'
import type { StudioDesktopBridge } from '@repo/studio-desktop-bridge/capabilities'
import { getStudioDesktopBridge, isStudioDesktop } from '@repo/studio-desktop-bridge/runtime'
import { getUserAuth } from 'auth/fetchers'
import { registerUserAuthTokenPersister } from 'codegen/fetcher/user-auth-token-persister'
import { resolveCookie, resolveToken } from 'codegen/fetcher'
import { desktopSignedOutUrl } from '../desktop/signed-out-url'
import { isDesktopBearerStale, readDesktopJwtClaims } from './desktop-jwt'

async function persistUserAuthToken(token: string | undefined) {
  if (!token) {
    return
  }

  if (isStudioDesktop()) {
    await persistDesktopUserAuthToken(token)
    return
  }

  // Cookie-authenticated hosts never keep a bearer in localStorage: the CF
  // domain has the WordPress cookie, and the first-party session hosts
  // (www.cf.land, www.idea.ai, freemode.ai) have the HttpOnly fm_session set
  // by /auth/session. Re-saving the token the gateway returns would put every
  // request back on header auth, ahead of the cookie in the IAM ladder.
  if (isOnCFDomain() || usesFirstPartySessionBridge()) {
    return
  }

  saveToken(token, 'login')
}

registerUserAuthTokenPersister(persistUserAuthToken)

export function prepareAuthAtStartup() {
  const isDesktop = isStudioDesktop()

  if (isDesktop) {
    const token = resolveToken()
    if (token && isDesktopBearerStale(token)) {
      clearTokenFromLocalStorage()
    }
    return
  }

  // Cookie hosts authenticate with the WordPress cookie. A leftover bearer
  // would put every request back on header auth.
  if (isOnCFDomain() && resolveToken()) {
    logOut()
  }
}

async function persistDesktopUserAuthToken(token: string) {
  const stored = resolveToken()
  const incoming = readDesktopJwtClaims(token)
  const storedClaims = stored ? readDesktopJwtClaims(stored) : null
  const bridge = getStudioDesktopBridge()
  const expectedUserId = await readPinnedDesktopUserId(bridge)
  // A guest token must not replace the signed-in account. Startup drops an
  // expired bearer before this runs, so the pin is the only record left.
  if (
    incoming?.anonymous &&
    (expectedUserId || (storedClaims && !storedClaims.anonymous && storedClaims.userId))
  ) {
    return
  }

  if (expectedUserId && incoming?.userId && expectedUserId !== incoming.userId) {
    await rejectDesktopIdentityMismatch(bridge)
    return
  }

  saveTokenToLocalStorage(token)
  saveRpcToken(token)
  void bridge?.auth?.setRpcToken(token)?.catch(() => undefined)
}

async function readPinnedDesktopUserId(bridge: StudioDesktopBridge | null): Promise<string | null> {
  const getSession = bridge?.auth?.getSession
  if (!getSession) {
    return null
  }
  try {
    const session = await getSession()
    if (!session || !('desktopUserId' in session)) {
      return null
    }
    return session.desktopUserId ?? null
  } catch {
    return null
  }
}

async function rejectDesktopIdentityMismatch(bridge: StudioDesktopBridge | null) {
  if (bridge?.auth?.requestLogout) {
    await bridge.auth.requestLogout('identity_mismatch')
  } else {
    await logOut()
    invalidateToken()
    await bridge?.auth?.setRpcToken('')?.catch(() => undefined)
  }
  try {
    window.location.replace(desktopSignedOutUrl(window.location.href))
  } catch {
    // Some hosts reject navigation; the cleared session is what the next load sees.
  }
}

function shouldPrimeUserAuthAtStartup() {
  const isDesktop = isStudioDesktop()

  if (isOnCFDomain() && !isDesktop) {
    return !resolveCookie()
  }

  return true
}

/**
 * Fetches userAuth once at startup when the session is not already established.
 * Token persistence for preview/desktop runs in the GraphQL fetcher when the
 * userAuth response settles, before other gated requests are released.
 */
export async function primeSessionAuth(queryClient: QueryClient) {
  if (!shouldPrimeUserAuthAtStartup()) {
    return
  }

  // The desktop account area shows a pending state until this settles, so it
  // must neither pause on a wrong offline guess nor sit through the retry
  // backoff when offline.
  const options = isStudioDesktop() ? ({ networkMode: 'always', retry: false } as const) : {}
  await getUserAuth(queryClient, options).catch(() => null)
}

export async function checkAndHandleAuth(queryClient: QueryClient) {
  prepareAuthAtStartup()
  await primeSessionAuth(queryClient)
}
